import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/authenticatedApi';
import { logger } from '../utils/logger';

type CliAuthStatus = 'ready' | 'processing' | 'success' | 'denied' | 'error';

export const CliAuthPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow_id') || '';
  const userCode = searchParams.get('user_code') || '';
  const hasFlow = Boolean(flowId && userCode);
  const [status, setStatus] = useState<CliAuthStatus>(hasFlow ? 'ready' : 'error');
  const [message, setMessage] = useState(
    hasFlow ? '' : 'This CLI authorization link is incomplete.',
  );

  const decide = async (decision: 'complete' | 'deny') => {
    if (!hasFlow || status === 'processing') return;
    setStatus('processing');
    setMessage(decision === 'complete' ? 'Authorizing your CLI session...' : 'Denying access...');

    try {
      await api.post(`/auth/cli/flows/${encodeURIComponent(flowId)}/${decision}`, { userCode });
      if (decision === 'complete') {
        setStatus('success');
        setMessage('CLI session authorized. You can return to your terminal.');
      } else {
        setStatus('denied');
        setMessage('CLI authorization denied. No credentials were issued.');
      }
    } catch (error: unknown) {
      logger.error('[CLI Auth] Failed to decide CLI login flow', { error });
      const err = error as {
        response?: { data?: { code?: string; message?: string; error?: string } };
      };
      const detail = err.response?.data?.message || err.response?.data?.error || '';
      setStatus('error');
      setMessage(detail || 'Unable to update this CLI authorization.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-6">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <div className="text-center">
          {(status === 'ready' || status === 'processing') && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Authorize Autar CLI
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-5">
                Only continue if this code matches the code currently shown in your terminal.
              </p>
              <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-4 py-3 font-mono text-2xl tracking-widest text-gray-900 dark:text-white mb-5">
                {userCode || 'Missing code'}
              </div>
              {status === 'processing' ? (
                <>
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500 dark:border-teal-400 mx-auto mb-3"></div>
                  <p className="text-gray-600 dark:text-gray-400">{message}</p>
                </>
              ) : (
                <div className="flex gap-3 justify-center">
                  <button
                    type="button"
                    onClick={() => void decide('complete')}
                    className="px-6 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors"
                  >
                    Authorize CLI
                  </button>
                  <button
                    type="button"
                    onClick={() => void decide('deny')}
                    className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Deny
                  </button>
                </div>
              )}
            </>
          )}

          {status === 'success' && (
            <>
              <div className="text-green-500 dark:text-green-400 text-5xl font-bold mb-4">OK</div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                CLI Authorized
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
              <Link
                to="/dashboard"
                className="inline-block px-6 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors"
              >
                Go to Dashboard
              </Link>
            </>
          )}

          {status === 'denied' && (
            <>
              <div className="text-gray-500 dark:text-gray-400 text-5xl font-bold mb-4">×</div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                CLI Authorization Denied
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
              <Link to="/dashboard" className="text-teal-600 dark:text-teal-400 underline">
                Return to Dashboard
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-red-500 dark:text-red-400 text-5xl font-bold mb-4">!</div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                CLI Authorization Failed
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
              <Link to="/dashboard" className="text-teal-600 dark:text-teal-400 underline">
                Return to Dashboard
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CliAuthPage;
